import { BackgroundGeolocationPlugin, CallbackError } from '@capacitor-community/background-geolocation';
import { registerPlugin } from '@capacitor/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { useEffect, useRef, useState } from 'react';

import { capacitorService } from '../../../../application/CapacitorService';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { CurrentPositionType } from '../../locationContext/reducer';

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(`BackgroundGeolocation`);

const defaultSettings = {
  enableHighAccuracy: true,
  timeout: Infinity,
  maximumAge: 0,
};

export type NewCoordsProps = {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
};

export type ErrorProps = {
  positionError?: GeolocationPositionError;
  customError?: string;
};

interface UsePositionProps {
  onNewCoords: (newCoords: NewCoordsProps, currentPosition?: CurrentPositionType) => any;
  onError?: ({ positionError, customError }: ErrorProps) => any;
  settings?: {
    enableHighAccuracy: boolean;
    timeout: number;
    maximumAge: number;
  };
}

export const usePosition = ({ onNewCoords, onError, settings = defaultSettings }: UsePositionProps) => {
  const [isTracking, setIsTracking] = useState(true);
  const watcherIdRef = useRef<string | null>(null);

  const onChange = (position: Position | null, err?: any) => {
    if (err) {
      if (onError) {
        onError({ positionError: err });
      }
      return;
    }
    if (!position) {
      if (onError) {
        onError({ customError: `No position` });
      }
      return;
    }

    const lat = parseFloat(position.coords.latitude.toFixed(5));
    const lng = parseFloat(position.coords.longitude.toFixed(5));

    onNewCoords({
      latitude: lat,
      longitude: lng,
      accuracy: position.coords.accuracy,
      timestamp: +new Date(),
    });
  };

  const removeWatcher = () => {
    if (watcherIdRef.current) {
      if (capacitorService.isNative()) {
        BackgroundGeolocation.removeWatcher({ id: watcherIdRef.current });
      } else {
        Geolocation.clearWatch({ id: watcherIdRef.current });
      }
      watcherIdRef.current = null;
    }
  };

  // https://github.com/capacitor-community/background-geolocation?tab=readme-ov-file

  useEffect(() => {
    if (!isTracking) {
      removeWatcher();
      return;
    }
    if (watcherIdRef.current) {
      return;
    }
    if (capacitorService.isNative()) {
      const startNativeWatcher = async () => {
        if (!watcherIdRef.current) {
          const watcher = BackgroundGeolocation.addWatcher(
            {
              backgroundMessage: intl.formatMessage({ id: `drivers.android.tracking.message` }),
              backgroundTitle: intl.formatMessage({ id: `drivers.android.tracking.title` }),
              requestPermissions: true,
              stale: false,
              // The minimum number of metres between subsequent locations. Defaults
              // to 0. Set to 10 to only receive an update when the device has moved
              // at least 10m.
              distanceFilter: 10,
            },
            function callback(location, error: CallbackError | undefined) {
              if (error) {
                if (error.code === `NOT_AUTHORIZED`) {
                  if (
                    window.confirm(`This app needs your location, but does not have permission.\n\nOpen settings now?`)
                  ) {
                    // It can be useful to direct the user to their device's
                    // settings when location permissions have been denied. The
                    // plugin provides the 'openSettings' method to do exactly
                    // this.
                    BackgroundGeolocation.openSettings();
                  }
                }
                return console.error(error);
              }

              if (!location) {
                if (onError) {
                  onError({ customError: `No location` });
                }
                return;
              }

              onNewCoords({
                latitude: location.latitude,
                longitude: location.longitude,
                accuracy: location.accuracy,
                timestamp: location.time || Date.now(),
              });
            }
          );
          watcherIdRef.current = await watcher;
        }
      };

      startNativeWatcher();
      return () => {
        removeWatcher();
      };
    } else {
      const geo = navigator.geolocation;
      if (!geo) {
        console.warn(`Geolocation is not supported`);
        if (onError) {
          onError({ customError: `Geolocation is not supported` });
        }
        return () => ({});
      }
      const startWebWatcher = async () => {
        if (!watcherIdRef.current) {
          const watcher = Geolocation.watchPosition({ enableHighAccuracy: true }, onChange);
          watcherIdRef.current = await watcher;
        }
      };

      startWebWatcher();

      return () => {
        removeWatcher();
      };
    }
    // eslint-disable-next-line
  }, [settings, isTracking]);
  return { setIsTracking };
};
